/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { type Session } from '../../lib/api/sessions.js';
import { istDayKey } from '../../lib/utils/datetime.js';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

function pad(n: number): string {
  return n < 10 ? `0${String(n)}` : String(n);
}

interface Cell {
  readonly key: string | null;
  readonly day: number;
}

/** Build the month grid (leading blanks + day cells) for the given anchor. */
function monthCells(anchor: Date): { cells: readonly Cell[]; label: string } {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const startWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: Cell[] = [];
  for (let i = 0; i < startWeekday; i += 1) {
    cells.push({ key: null, day: 0 });
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push({ key: `${String(year)}-${pad(month + 1)}-${pad(d)}`, day: d });
  }
  const label = new Date(year, month, 1).toLocaleString('en', { month: 'long', year: 'numeric' });
  return { cells, label };
}

function DayCell({
  cell,
  sessions,
  clientNames,
}: {
  readonly cell: Cell;
  readonly sessions: readonly Session[];
  readonly clientNames: ReadonlyMap<string, string>;
}): JSX.Element {
  const { t } = useTranslation();
  if (cell.key === null) {
    return <div className="min-h-24 rounded-xl bg-slate-50/40 dark:bg-slate-800/20" />;
  }
  const shown = sessions.slice(0, 3);
  const extra = sessions.length - shown.length;
  return (
    <div className="min-h-24 rounded-xl border border-slate-100 p-2 dark:border-slate-800">
      <p className="mb-1 text-xs font-semibold text-slate-500 dark:text-slate-400">{cell.day}</p>
      <div className="space-y-1">
        {shown.map((s) => (
          <p
            key={s.id}
            className="bg-[var(--accent)]/10 truncate rounded-md px-1.5 py-0.5 text-[11px] text-[var(--accent-strong)]"
            title={clientNames.get(s.clientId) ?? ''}
          >
            {clientNames.get(s.clientId) ?? '—'}
          </p>
        ))}
        {extra > 0 && (
          <p className="text-[11px] text-slate-400">
            {t('admin.sessions.moreCount', { count: extra })}
          </p>
        )}
      </div>
    </div>
  );
}

/** Month calendar view of clinic sessions (Figma Schedule → Month). */
export function MonthCalendar({
  sessions,
  clientNames,
}: {
  readonly sessions: readonly Session[];
  readonly clientNames: ReadonlyMap<string, string>;
}): JSX.Element {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<Date>(() => new Date());

  const byDay = useMemo(() => {
    const map = new Map<string, Session[]>();
    for (const s of sessions) {
      const key = istDayKey(s.scheduledStart);
      const list = map.get(key);
      if (list === undefined) {
        map.set(key, [s]);
      } else {
        list.push(s);
      }
    }
    return map;
  }, [sessions]);

  const { cells, label } = useMemo(() => monthCells(anchor), [anchor]);
  const prev = useCallback(() => {
    setAnchor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  }, []);
  const next = useCallback(() => {
    setAnchor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  }, []);
  const today = useCallback(() => {
    setAnchor(new Date());
  }, []);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 dark:text-white">{label}</h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={prev}
            aria-label={t('common.previous')}
            className="focus-ring rounded-lg p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={today}
            className="focus-ring rounded-lg px-3 py-1 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            {t('admin.sessions.today')}
          </button>
          <button
            type="button"
            onClick={next}
            aria-label={t('common.next')}
            className="focus-ring rounded-lg p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="mb-1 grid grid-cols-7 gap-1 text-center text-xs font-medium text-slate-400">
        {WEEKDAYS.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, i) => (
          <DayCell
            key={cell.key ?? `blank-${String(i)}`}
            cell={cell}
            sessions={cell.key !== null ? (byDay.get(cell.key) ?? []) : []}
            clientNames={clientNames}
          />
        ))}
      </div>
    </div>
  );
}
